// ---------------------------
// API Base URL Selection
// ---------------------------
// Priority:
// 1) REACT_APP_API_BASE (env override)
// 2) If running on appraise.hestonautomotive.com => use same-origin ''
// 3) If localhost => local backend
// 4) Fallback => same-origin ''
const PROD_FRONTEND_HOSTS = ['appraise.hestonautomotive.com'];
const PROD_API = 'https://api.hestonautomotive.com'; // ✅ point to production API subdomain

const inferApiBase = () => {
  try {
    const envBase = process.env.REACT_APP_API_BASE?.trim();
    if (envBase) return envBase;

    const host =
      (typeof window !== 'undefined' && window.location?.hostname) || '';

    if (PROD_FRONTEND_HOSTS.includes(host)) return PROD_API;

    if (host === 'localhost' || host === '127.0.0.1') {
      // Dev: direct local backend
      return 'http://localhost:5000';
    }

    // Safe default for previews/other hosts: same-origin
    return '';
  } catch {
    return '';
  }
};

export const API_BASE = inferApiBase();


// ---------------------------
// Internal helpers
// ---------------------------
const isFormData = (body) =>
  body &&
  typeof body === 'object' &&
  typeof body.append === 'function' &&
  body.constructor?.name === 'FormData';

const mergeHeaders = (base = {}, extra = {}) => {
  const clean = (obj) =>
    Object.fromEntries(
      Object.entries(obj || {}).filter(([, v]) => v !== undefined && v !== null)
    );
  return { ...clean(base), ...clean(extra) };
};

const parseResponse = async (res) => {
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await res.json().catch(() => ({}));
    return data;
  }
  const text = await res.text();
  return text;
};

const buildError = (res, payload) => {
  const message =
    (payload && (payload.error || payload.message)) ||
    `Request failed with status ${res.status}`;
  const err = new Error(message);
  err.status = res.status;
  err.payload = payload;
  return err;
};

// ---------------------------
// Core HTTP wrapper (fetch)
// ---------------------------
export async function http(
  path,
  { method = 'GET', body, headers, signal } = {}
) {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

  // Default headers: only set Content-Type for JSON bodies
  const defaultHeaders = {};
  let reqBody = body;

  if (body && !isFormData(body)) {
    defaultHeaders['Content-Type'] = 'application/json';
    reqBody = JSON.stringify(body);
  }

  const res = await fetch(url, {
    method,
    headers: mergeHeaders(defaultHeaders, headers),
    body: reqBody,
    credentials: 'include', // IMPORTANT for Safari/iOS cookies
    mode: 'cors',
    redirect: 'follow',
    cache: 'no-store',
    signal,
  });

  const payload = await parseResponse(res);

  if (!res.ok) {
    // (Optional) central auth handling
    // if (res.status === 401) window.location.assign('/login');
    throw buildError(res, payload);
  }
  return payload;
}

// ---------------------------
// Convenience methods
// ---------------------------
export const get = (path, opts) => http(path, { ...opts, method: 'GET' });
export const del = (path, opts) => http(path, { ...opts, method: 'DELETE' });
export const post = (path, body, opts) =>
  http(path, { ...opts, method: 'POST', body });
export const put = (path, body, opts) =>
  http(path, { ...opts, method: 'PUT', body });

// Upload helper for FormData (images/files). Do NOT pass Content-Type manually.
export const upload = async (path, formData, opts = {}) => {
  if (!isFormData(formData)) {
    throw new Error('upload() expects a FormData instance');
  }
  const res = await fetch(
    `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`,
    {
      method: opts.method || 'POST',
      body: formData,
      headers: mergeHeaders({}, opts.headers), // don't set Content-Type for FormData
      credentials: 'include',
      mode: 'cors',
      redirect: 'follow',
      cache: 'no-store',
      signal: opts.signal,
    }
  );

  const payload = await parseResponse(res);
  if (!res.ok) throw buildError(res, payload);
  return payload;
};

// ---------------------------
// Typed API surface
// ---------------------------
// Auth
export const api = {
  me: () => get('/api/auth/me'),
  adminLogin: (username, password) =>
    post('/api/auth/admin/login', { username, password }),
  staffLogin: (username, password) =>
    post('/api/auth/staff/login', { username, password }),

  // Admin > Staff 2FA Toggle
  toggleStaff2FA: (id, enable) => put(`/api/staff/${id}/toggle-2fa`, { enable }),
  logout: () => post('/api/auth/logout'),

  // Staff
  listStaff: () => get('/api/staff'),
  createStaff: (payload) => post('/api/staff', payload),
  deleteStaff: (id) => del(`/api/staff/${id}`),
  updateStaff: (id, data) => put(`/api/staff/${id}`, data),
  resetStaffPassword: (id) => post(`/api/staff/${id}/reset-password`),
  setStaffPassword: (id, { password }) =>
    put(`/api/staff/${id}/password`, { password }),

  // 2FA
  setup2FA: () => post('/api/auth/2fa/setup'),
  verify2FA: (token) => post('/api/auth/2fa/verify-setup', { token }),
  disable2FA: () => post('/api/auth/2fa/disable'),
  validate2FALogin: (token) => post('/api/auth/2fa/validate-login', { token }),

  // DVLA
  lookupVehicle: (reg) => post('/api/dvla/lookup', { reg }),

  // Appraisals
  createAppraisal: (payload) => post('/api/appraisals', payload),
  updateAppraisal: (id, payload) => put(`/api/appraisals/${id}`, payload),
  deleteAppraisal: (id) => del(`/api/appraisals/${id}`),
  getAllAppraisals: () => get('/api/appraisals/admin'),
  getMyAppraisals: () => get('/api/appraisals/mine'),
  getAppraisalById: (id) => get(`/api/appraisals/${id}`),
  // Approval status update
  updateApprovalStatus: async (appraisalId, status, message) => {
    try {
      return await http(`/api/appraisals/${appraisalId}/approval`, {
        method: 'PATCH',
        body: { approvalStatus: status, adminMessage: message }
      });
    } catch (error) {
      console.error('Update approval error:', error);
      throw error;
    }
  },

  // Upload a fault photo and get back a hosted URL (with graceful fallback)
  uploadFaultPhoto: async (dataUrl) => {
    try {
      const res = await post('/api/uploads/fault-photo', { dataUrl });
      if (res && res.url) return res.url;
      throw new Error('Photo upload failed: missing URL');
    } catch (e) {
      console.warn('uploadFaultPhoto: backend not found, falling back to base64.', e);
      // Fallback so the app doesn't crash and the PDF can still embed the image
      return dataUrl; // base64 data URL will still render in the PDF
    }
  },

  // ==================== DOCUMENT UPLOAD FUNCTIONS ====================

  // Upload documents (uses the upload helper for FormData)
  uploadDocuments: async (formData) => {
    try {
      return await upload('/api/documents/upload', formData);
    } catch (error) {
      console.error('Upload documents error:', error);
      throw error;
    }
  },

  // Get all uploaded documents (with optional filters)
  getUploadedDocuments: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.regNumber) {
        queryParams.append('regNumber', filters.regNumber);
      }

      if (filters.docType) {
        queryParams.append('docType', filters.docType);
      }

      const queryString = queryParams.toString();
      const path = queryString
        ? `/api/documents/list?${queryString}`
        : '/api/documents/list';

      return await get(path);
    } catch (error) {
      console.error('Get documents error:', error);
      throw error;
    }
  },

  // Get documents by registration number
  getDocumentsByRegistration: async (regNumber) => {
    try {
      return await get(`/api/documents/registration/${regNumber}`);
    } catch (error) {
      console.error('Get documents by registration error:', error);
      throw error;
    }
  },

  // Get grouped documents (organized by registration)
  getGroupedDocuments: async () => {
    try {
      return await get('/api/documents/grouped');
    } catch (error) {
      console.error('Get grouped documents error:', error);
      throw error;
    }
  },

  // Delete a document
  deleteDocument: async (documentId) => {
    try {
      return await del(`/api/documents/${documentId}`);
    } catch (error) {
      console.error('Delete document error:', error);
      throw error;
    }
  },

  // Get document statistics
  getDocumentStats: async () => {
    try {
      return await get('/api/documents/stats');
    } catch (error) {
      console.error('Get document stats error:', error);
      throw error;
    }
  },

  // Helper to get file URL
  getDocumentFileUrl: (regNumber, filename) => {
    return `${API_BASE}/api/documents/file/${regNumber}/${filename}`;
  },

  // ==================== TASK MANAGEMENT ====================
  getAllTasks: () => get('/api/tasks'),
  getStaffTasks: (staffId) => get(`/api/tasks/staff/${staffId}`),
  createTask: (data) => post('/api/tasks', data),
  updateTask: (id, data) => put(`/api/tasks/${id}`, data),
  deleteTask: (id) => del(`/api/tasks/${id}`),

  // ==================== NOTIFICATIONS ====================
  getNotifications: () => get('/api/notifications'),
  markNotificationAsRead: (id) => http(`/api/notifications/${id}/read`, { method: 'PATCH' }),
  markAllNotificationsAsRead: () => http('/api/notifications/mark-all-read', { method: 'PATCH' }),
  deleteNotification: (id) => del(`/api/notifications/${id}`),

  // Upload PDF template and get parsed faults
  uploadTaskTemplatePdf: async (file) => {
    const formData = new FormData();
    formData.append('templatePdf', file);
    return await upload('/api/tasks/upload-template', formData);
  },
};

export default api;
